import { canv } from "@/canv/global.js";
import { getTarget } from "@/canv/utils.js";
import { MAX_TEXT_SIZE, MIN_TEXT_SIZE } from "@/constants";
import { getFontSize } from "@/utils";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import { wmBase } from "@/widgetMenu/wmBase";

const ALIASES: Record<number, string> = {
    20: "Маленький",
    30: "Средний",
    45: "Большой",
    55: "Очень большой",
    65: "Огромный",
};

export class wmTextSize extends wmBase {
    public name = "wm_textSize";
    public options: widgetOption<number>[];
    public value = 30;
    private isSubmitted = false;

    constructor(options: widgetOption<number>[]) {
        super();
        this.options = options;
    }

    public build(): string {
        const uniqValues = Array.from(new Set(this.options.map((opt) => opt.value)));
        const isMixed = uniqValues.length !== 1;
        const currentValue = uniqValues[0] ?? this.value;
        let displayValue: string | number = "Смешанный размер";

        if (!isMixed) {
            displayValue = ALIASES[currentValue] ?? Math.round(currentValue);
        }

        this.value = currentValue;

        return `
            <div class="wm_textSize">
                <p>${displayValue}</p>
            </div>
        `;
    }

    public init(): void {
        const item = document.querySelector<HTMLElement>(".wm_textSize");
        if (!item) return;

        item.removeEventListener("mousedown", this.showSubMenu);
        item.addEventListener("mousedown", this.showSubMenu);
    }

    private showSubMenu = (_e: MouseEvent): void => {
        const existing = document.querySelector<HTMLElement>(".wm_textSize_submenu");
        if (existing) {
            existing.outerHTML = "";
            return;
        }

        const existingSubMenu = document.querySelector<HTMLElement>(".widgetSubMenu");
        if (existingSubMenu) {
            existingSubMenu.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = this.name;
        const submenu = document.createElement("div");
        submenu.classList.add("widgetSubMenu", "wm_textSize_submenu");
        submenu.style.opacity = "0";
        document.body.prepend(submenu);

        Object.entries(ALIASES).forEach(([size, alias]) => {
            submenu.innerHTML += this.submenuTemplate(alias, Number(size));
        });

        submenu.innerHTML += `
              <div class="wm_textSize_submenu_dividor"></div>
              <input
                type="number"
                step="1"
                min="${MIN_TEXT_SIZE}"
                max="${MAX_TEXT_SIZE}"
                class="wm_textSize_submenu_input"
                value="${this.value}"
              />
        `;

        submenu.querySelectorAll<HTMLElement>(".wm_textSize_submenu_item").forEach((item) => {
            item.removeEventListener("mouseup", this.itemClick);
            item.addEventListener("mouseup", this.itemClick);
        });

        const input = submenu.querySelector<HTMLInputElement>(".wm_textSize_submenu_input");
        if (!input) return;

        input.removeEventListener("focus", this.inputFocus);
        input.addEventListener("focus", this.inputFocus);

        input.removeEventListener("blur", this.inputUnFocus);
        input.addEventListener("blur", this.inputUnFocus);

        input.removeEventListener("keypress", this.inputSubmit);
        input.addEventListener("keypress", this.inputSubmit);

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 50);
    };

    private inputFocus = (): void => {
        canv.appInterface.inputFocused = 1;
    };

    private inputUnFocus = (): void => {
        canv.appInterface.inputFocused = this.isSubmitted ? 2 : 0;
    };

    private inputSubmit = (e: KeyboardEvent): void => {
        if (e.key !== "Enter") return;
        const input = e.target as HTMLInputElement;
        const size = getFontSize(Number.parseInt(input.value, 10));
        if (!size) return;

        this.isSubmitted = true;
        this.applyValue(this.options, size);
        this.value = size;

        const menuItem = document.querySelector<HTMLElement>(".wm_textSize");
        if (menuItem) {
            const p = menuItem.querySelector<HTMLParagraphElement>("p");
            if (p) {
                p.innerText = ALIASES[size] ?? Math.round(size).toString();
            }
        }
    };

    private itemClick = (e: MouseEvent): void => {
        const item = getTarget(e, "wm_textSize_submenu_item") as HTMLElement;
        const sizeValue = item.getAttribute("data-size-value");
        if (sizeValue) {
            const size = getFontSize(Number.parseInt(sizeValue, 10));
            if (!size) return;

            this.applyValue(this.options, size);
            this.value = size;

            const menuItem = document.querySelector<HTMLElement>(".wm_textSize");
            if (menuItem) {
                const p = menuItem.querySelector("p");
                if (p) {
                    p.innerText = ALIASES[size] ?? Math.round(size).toString();
                }
            }
        }
    };

    private submenuTemplate(alias: string, size: number): string {
        return `
          <div class="wm_textSize_submenu_item" data-size-value="${size}">
            <p>${alias}</p>
          </div>
        `;
    }
}
